import React, { useState, useCallback } from 'react';
import api from '../../api/api';

/**
 * Read our stock and SAP's stock at the same moment and show both.
 *
 * The event log is our own account of our own work, so it can only ever show that we
 * did what we thought we were told. Answering "is MRP actually right" needs a live
 * read of both sides, which is what this does — per warehouse, so a single bad
 * warehouse cannot hide inside a matching total.
 *
 * On demand, not on page load: it opens a session against the client's production
 * ERP, and no screen should do that merely because someone opened it.
 */

const TH = 'px-2 py-1 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide';
const TD = 'px-2 py-1 text-sm border-t border-gray-100';

function num(n) {
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 1000) / 1000);
}

export default function VerifyAgainstSap({ itemCode }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const verify = useCallback(async () => {
    setBusy(true);
    setError('');
    setState(null);
    try {
      const res = await api.get(`/sap/webhook/verify?item=${encodeURIComponent(itemCode)}`);
      setState(res);
    } catch (e) {
      setError(e.message || 'Could not verify against SAP');
    } finally {
      setBusy(false);
    }
  }, [itemCode]);

  return (
    <div className="mt-2">
      <button
        onClick={verify}
        disabled={busy}
        className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
      >
        {busy ? 'Checking SAP…' : 'Verify against SAP now'}
      </button>

      {error && <div className="mt-2 text-xs text-red-700">{error}</div>}

      {state && (
        <div className="mt-2 border border-gray-200 rounded p-2 bg-gray-50">
          <div className="text-xs mb-2">
            {state.mismatches === 0 ? (
              <span className="text-green-700 font-medium">
                ✓ Our stock matches SAP exactly — {state.matches} warehouse
                {state.matches === 1 ? '' : 's'} checked
              </span>
            ) : (
              <span className="text-red-700 font-medium">
                {state.mismatches} of {state.warehouses.length} warehouses do not match
              </span>
            )}
            <span className="text-gray-400"> · checked {new Date(state.checkedAt).toLocaleTimeString()}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className={TH}>Warehouse</th>
                  <th className={TH}>In our system</th>
                  <th className={TH}>In SAP</th>
                  <th className={TH}>Committed (ours / SAP)</th>
                  <th className={TH}>Agrees</th>
                </tr>
              </thead>
              <tbody>
                {state.warehouses.map((w) => (
                  <tr key={w.warehouse} className={w.agrees ? '' : 'bg-red-50'}>
                    <td className={`${TD} font-mono text-xs`}>WH{w.warehouse}</td>
                    <td className={`${TD} font-medium`}>
                      {w.weHaveNoRow ? <span className="text-gray-400">no row</span> : num(w.ours.in_stock)}
                    </td>
                    <td className={TD}>{num(w.sap.in_stock)}</td>
                    <td className={`${TD} text-gray-500 text-xs`}>
                      {num(w.ours.committed)} / {num(w.sap.committed)}
                    </td>
                    <td className={TD}>
                      {w.agrees
                        ? <span className="text-green-700">✓</span>
                        : <span className="text-red-700 font-medium">✗</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
